// Operator dashboard — prototype-faithful (chitos-prototype/dashboard.jsx).
// Real data via the existing orpc.dashboard.* queries; layout + classes
// ported from the prototype. Fields the backend doesn't supply (delivery
// status, collector, GPS) are omitted gracefully.

import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { current_language } from '../i18n/index.js';
import { format_inr } from '../lib/format.js';
import { orpc } from '../lib/orpc.js';
import { Icon } from '../components/Icon.js';
import { NotifyMenu } from '../components/NotifyMenu.js';
import { OnboardingChecklist } from '../components/OnboardingChecklist.js';

interface Subscriber { id: string; name_ta: string; name_en: string; phone: string }
interface Chit {
  id: string;
  name_ta: string;
  name_en: string;
  pool_size: number;
  duration_months: number;
  monthly_subscription_paise: number;
  member_count?: number;
  current_month_collection_rate?: number;
  current_month_pointer?: number;
}
interface DueRow {
  id: string;
  expected_amount_paise: number;
  total_paid_paise: number;
  status: string;
  subscriber: Subscriber | null;
  chit: (Chit & { position?: number }) | null;
}
interface ActivityItem {
  id: string;
  kind: 'payment' | 'whatsapp' | 'reminder';
  timestamp: string;
  title: string;
  subscriber_name_ta: string | null;
  subscriber_name_en: string | null;
}

function initials(name: string | null | undefined): string {
  if (!name) return '··';
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '··';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]!.charAt(0) + p[p.length - 1]!.charAt(0)).toUpperCase();
}

type DueFilter = 'all' | 'pending' | 'overdue' | 'paid';

export function DashboardPage(): ReactElement {
  const lang = current_language();
  const nav = useNavigate();
  const [filter, set_filter] = useState<DueFilter>('all');

  const kpis_q = useQuery({ queryKey: ['dash-kpis'], queryFn: () => orpc.dashboard.kpis({}) });
  const today_q = useQuery({ queryKey: ['dash-today'], queryFn: () => orpc.dashboard.today_collection({}) });
  const chits_q = useQuery({ queryKey: ['dash-active-chits'], queryFn: () => orpc.dashboard.active_chits({}) });
  const activity_q = useQuery({ queryKey: ['dash-activity'], queryFn: () => orpc.dashboard.activity_feed({}) });

  const k = kpis_q.data?.kpis;
  const dues = (today_q.data?.rows ?? []) as DueRow[];
  const chits = (chits_q.data?.chits ?? []) as Chit[];
  const items = (activity_q.data?.items ?? []) as ActivityItem[];

  const counts = useMemo(
    () => ({
      all: dues.length,
      pending: dues.filter((d) => d.status === 'pending').length,
      overdue: dues.filter((d) => d.status === 'overdue').length,
      paid: dues.filter((d) => d.status === 'paid').length,
    }),
    [dues],
  );
  const filtered = filter === 'all' ? dues : dues.filter((d) => d.status === filter);

  const wa_feed = items.filter((a) => a.kind === 'whatsapp' || a.kind === 'reminder');

  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);

  // KPI cards — mapped to the real dashboard.kpis fields.
  const kpiCards = [
    {
      label: t('இன்றைய நிலுவை', "Today's Dues"),
      value: k ? format_inr(k.today_dues_paise) : '—',
      sub: k ? `${k.today_dues_count} ${t('உறுப்பினர்', 'members')}` : '',
      accent: 'primary' as const,
    },
    {
      label: t('வசூலிக்கப்பட்டது', 'Collected'),
      value: k ? format_inr(k.today_collected_paise) : '—',
      sub: k && k.today_dues_count > 0
        ? `${Math.round((k.today_collected_paise / Math.max(k.today_dues_paise, 1)) * 100)}% ${t('வசூல்', 'collected')}`
        : '',
      accent: undefined,
    },
    {
      label: t('நிலுவைதாரர்கள்', 'Defaulters'),
      value: k ? `${k.defaulters_count}` : '—',
      sub: '',
      accent: 'warn' as const,
    },
    {
      label: t('செயலில் சீட்டுகள்', 'Active Chits'),
      value: k ? `${k.active_chits_count}` : '—',
      sub: '',
      accent: undefined,
    },
    {
      label: t('உறுப்பினர்கள்', 'Members'),
      value: k ? `${k.total_subscribers_count}` : '—',
      sub: '',
      accent: undefined,
    },
    {
      label: t('மாத வசூல்', 'Month Collection'),
      value: k ? `${Math.round(k.mtd_collection_rate * 100)}%` : '—',
      sub: k ? `${format_inr(k.mtd_collected_paise)} / ${format_inr(k.mtd_expected_paise)}` : '',
      accent: undefined,
    },
  ];

  return (
    <div className="page">
      <div>
        <div className="page-head">
          <div>
            <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>
              {t('வணக்கம் 🪔', 'Welcome')}
            </div>
            <div className="h-sub">
              {k
                ? t(
                    `இன்று ${k.today_dues_count} சந்தாக்கள் வசூலுக்கு வரவுள்ளன`,
                    `${k.today_dues_count} subscriptions due today`,
                  )
                : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={() => nav('/reports')}>
              <Icon.download /> {t('ஏற்றுமதி', 'Export')}
            </button>
            <button className="btn primary" onClick={() => nav('/chits/new')}>
              <Icon.plus /> {t('புதிய சீட்டு', 'New Chit')}
            </button>
          </div>
        </div>

        <OnboardingChecklist />

        <div className="kpis">
          {kpiCards.map((c, i) => (
            <Kpi key={i} {...c} lang={lang} />
          ))}
        </div>

        {/* Today's Collection */}
        <div className="card">
          <div className="card-head">
            <div className="card-title">{t('இன்றைய வசூல்', "Today's Collection")}</div>
            <div className="card-sub mono">
              {k ? `${format_inr(k.today_collected_paise)} / ${format_inr(k.today_dues_paise)}` : ''}
            </div>
            <div className="card-actions">
              {filtered.length > 0 && (
                <NotifyMenu
                  bulk={{
                    kind: 'dues',
                    due_month: new Date().toISOString().slice(0, 7),
                    status: ['pending', 'overdue'],
                  }}
                  estimated_count={filtered.length}
                  intent="due_reminder"
                  variant="inline"
                  label={t('அனைவருக்கும் நினைவூட்டு', 'Remind all')}
                />
              )}
            </div>
          </div>
          <div className="filters">
            {(['all', 'pending', 'overdue', 'paid'] as DueFilter[]).map((id) => (
              <button
                key={id}
                className={'chip' + (filter === id ? ' on' : '')}
                onClick={() => set_filter(id)}
              >
                <span className={lang === 'ta' ? 'tamil' : ''}>
                  {id === 'all'
                    ? t('அனைத்தும்', 'All')
                    : id === 'pending'
                      ? t('நிலுவை', 'Pending')
                      : id === 'overdue'
                        ? t('தாமதம்', 'Overdue')
                        : t('செலுத்திய', 'Paid')}
                </span>
                <span className="ct">{counts[id]}</span>
              </button>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>
              {filtered.length} {t('வரிசைகள்', 'rows')}
            </span>
          </div>
          <div style={{ maxHeight: 460, overflow: 'auto' }}>
            <table className="dues-table">
              <thead>
                <tr>
                  <th>{t('உறுப்பினர்', 'Member')}</th>
                  <th>{t('சீட்டு', 'Chit')}</th>
                  <th style={{ textAlign: 'right' }}>{t('தொகை', 'Amount')}</th>
                  <th>{t('நிலை', 'Status')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const name = lang === 'ta' ? d.subscriber?.name_ta : d.subscriber?.name_en;
                  const chit_name = lang === 'ta' ? d.chit?.name_ta : d.chit?.name_en;
                  return (
                    <tr key={d.id}>
                      <td>
                        <div className="row-sub">
                          <div className="ava-sm">{initials(d.subscriber?.name_en)}</div>
                          <div>
                            <div className={lang === 'ta' ? 'tamil name-ta' : 'name-en'}>{name}</div>
                            <div className="name-meta mono">{d.subscriber?.phone}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="chit-pill">{chit_name}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="amount">{format_inr(d.expected_amount_paise)}</span>
                      </td>
                      <td>
                        {d.status === 'paid' && (
                          <span className="status paid">
                            <span className="dot" />
                            {t('செலுத்தினார்', 'Paid')}
                          </span>
                        )}
                        {d.status === 'pending' && (
                          <span className="status pending">
                            <span className="dot" />
                            {t('நிலுவை', 'Pending')}
                          </span>
                        )}
                        {d.status === 'partial' && (
                          <span className="status pending">
                            <span className="dot" />
                            {t('பகுதி', 'Partial')}
                          </span>
                        )}
                        {d.status === 'overdue' && (
                          <span className="status overdue">
                            <span className="dot" />
                            {t('தாமதம்', 'Overdue')}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="row-actions">
                          {d.status !== 'paid' ? (
                            <>
                              <button className="btn sm" title="WhatsApp">
                                <Icon.wa />
                              </button>
                              <button className="btn sm primary">
                                <Icon.check /> {t('actions.mark_paid', 'Collect payment')}
                              </button>
                            </>
                          ) : (
                            <button className="btn sm ghost" title="Receipt">
                              <Icon.receipt />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: 'var(--ink-3)', padding: 18 }}>
                      {t('இன்று நிலுவை இல்லை', 'Nothing due today')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="section-band">{t('செயலிலுள்ள சீட்டுகள்', 'Active Chit Groups')}</div>
        <div className="chit-grid">
          {chits.map((c) => {
            const value = c.monthly_subscription_paise * c.pool_size;
            const rate = c.current_month_collection_rate ?? 0;
            const health = rate >= 0.8 ? 'good' : rate >= 0.5 ? 'watch' : 'bad';
            const current = c.current_month_pointer ?? 0;
            const pct = c.duration_months > 0 ? Math.min((current / c.duration_months) * 100, 100) : 0;
            return (
              <div key={c.id} className="chit-card" onClick={() => nav(`/chits/${c.id}`)} style={{ cursor: 'pointer' }}>
                <div className="chit-head">
                  <span className={'health-dot ' + health} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={'chit-title ' + (lang === 'ta' ? 'tamil' : '')}>
                      {lang === 'ta' ? c.name_ta : c.name_en}
                    </div>
                    <div className="chit-id">{c.id.slice(0, 8)}</div>
                  </div>
                </div>
                <div className="chit-meta">
                  <span>{t('மதிப்பு', 'Value')} <b>{format_inr(value)}</b></span>
                  <span>{t('மாதம்', 'Monthly')} <b>{format_inr(c.monthly_subscription_paise)}</b></span>
                  <span>{t('உறுப்பினர்', 'Members')} <b>{c.member_count ?? c.pool_size}</b></span>
                </div>
                <div>
                  <div className="progress"><span style={{ width: pct + '%' }} /></div>
                  <div className="progress-meta">
                    <span>{t('கட்டம்', 'Instalment')} {current}/{c.duration_months}</span>
                    {typeof c.current_month_collection_rate === 'number' && (
                      <span>{Math.round(rate * 100)}% {t('வசூல்', 'collected')}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {chits.length === 0 && (
            <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: 8 }}>
              {t('செயலில் சீட்டுகள் இல்லை', 'No active chits')}
            </div>
          )}
        </div>
      </div>

      {/* Right rail */}
      <aside className="rail">
        <div className="card">
          <div className="rail-title">
            <span style={{ color: '#25D366' }}><Icon.wa /></span>
            {t('வாட்ஸ்அப் செயல்பாடு', 'WhatsApp Activity')}
            <span className="count">{wa_feed.length}</span>
          </div>
          <div className="wa-list">
            {wa_feed.map((w) => (
              <div key={w.id} className="wa-row">
                <div className="wa-icon"><Icon.wa /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="wa-meta">
                    <span className={'wa-name ' + (lang === 'ta' && w.subscriber_name_ta ? 'tamil' : '')}>
                      {lang === 'ta' ? w.subscriber_name_ta ?? w.title : w.subscriber_name_en ?? w.title}
                    </span>
                    <span className={'wa-tag ' + (w.kind === 'reminder' ? 'reminder' : 'receipt')}>
                      {w.kind === 'reminder' ? t('நினைவூட்டல்', 'Remind') : t('வாட்ஸ்அப்', 'WhatsApp')}
                    </span>
                    <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--ink-3)' }} className="mono">
                      {new Date(w.timestamp).toLocaleTimeString(lang === 'ta' ? 'ta-IN' : 'en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className={'wa-preview ' + (lang === 'ta' ? 'tamil' : '')}>{w.title}</div>
                </div>
              </div>
            ))}
            {wa_feed.length === 0 && (
              <div style={{ color: 'var(--ink-3)', fontSize: 12, padding: '6px 2px' }}>
                {t('செயல்பாடு இல்லை', 'No activity yet')}
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="rail-title">{t('இன்றைய செயல்பாடு', "Today's Activity")}</div>
          <div className="activity">
            {items.map((a) => (
              <div key={a.id} className="activity-row">
                <span className={'activity-dot ' + (a.kind === 'payment' ? 'payment' : a.kind === 'reminder' ? 'alert' : 'wa')} />
                <div className={'activity-text ' + (lang === 'ta' ? 'tamil' : '')}>
                  {(lang === 'ta' ? a.subscriber_name_ta : a.subscriber_name_en) ?? ''} {a.title}
                </div>
                <span className="activity-time">
                  {new Date(a.timestamp).toLocaleTimeString(lang === 'ta' ? 'ta-IN' : 'en-IN', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
            {items.length === 0 && (
              <div style={{ color: 'var(--ink-3)', fontSize: 12, padding: '6px 2px' }}>
                {t('செயல்பாடு இல்லை', 'No activity yet')}
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

// KPI card — prototype .kpi with sparkline.
function Kpi(props: {
  label: string;
  value: string;
  sub?: string;
  accent?: 'primary' | 'warn';
  lang: string;
}): ReactElement {
  return (
    <div className={'kpi' + (props.accent === 'primary' ? ' primary' : props.accent === 'warn' ? ' warn' : '')}>
      <div className={'kpi-label ' + (props.lang === 'ta' ? 'tamil' : '')}>{props.label}</div>
      <div className="kpi-value">{props.value}</div>
      {props.sub && (
        <div className="kpi-sub">
          <span>{props.sub}</span>
        </div>
      )}
    </div>
  );
}
